import api from './axiosConfig';

const getConfig = () => {
    const userToken = localStorage.getItem('userToken');
    return {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userToken}`,
        },
    };
};

// Get all purchase orders
export const getPurchaseOrders = async (
    page,
    pageSize,
    searchTerm,
    vendorId,
    startDate,
    endDate,
    statusFilter,
    etaStartDate,
    etaEndDate
) => {
    try {
        const response = await api.get(
            '/api/purchase-orders',
            {
                ...getConfig(),
                params: {
                    page,
                    pageSize,
                    searchTerm,
                    vendorId,
                    startDate,
                    endDate,
                    statusFilter,
                    etaStartDate,
                    etaEndDate
                }
            }
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Get single purchase order with details
export const getPurchaseOrder = async (id) => {
    try {
        const response = await api.get(
            `/api/purchase-orders/${id}`,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Create new purchase order
export const createPurchaseOrder = async (poData) => {
    try {
        const response = await api.post(
            '/api/purchase-orders/create/',
            poData,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Update purchase order
export const updatePurchaseOrder = async (id, poData) => {
    try {
        console.log('Updating PO with ID:', id);
        console.log('Update data being sent:', JSON.stringify(poData, null, 2));
        const response = await api.put(
            `/api/purchase-orders/update/${id}/`,
            poData,
            getConfig()
        );
        return response.data;
    } catch (error) {
        console.error('Error updating PO:', error.response?.data || error.message);
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Delete purchase order
export const deletePurchaseOrder = async (id) => {
    try {
        const response = await api.delete(
            `/api/purchase-orders/${id}/delete/`,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Add line to purchase order
export const createPurchaseOrderLine = async (poId, lineData) => {
    try {
        const response = await api.post(
            `/api/purchase-orders/${poId}/lines/create/`,
            lineData,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Update purchase order line
export const updatePurchaseOrderLine = async (poId, lineId, lineData) => {
    try {
        const response = await api.put(
            `/api/purchase-orders/${poId}/lines/${lineId}/update/`,
            lineData,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Delete purchase order line
export const deletePurchaseOrderLine = async (poId, lineId) => {
    try {
        const response = await api.delete(
            `/api/purchase-orders/${poId}/lines/${lineId}/delete/`,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Get vendor item mapping
export const getVendorItemMapping = async (vendorId, stockItemId) => {
    try {
        const response = await api.get(
            `/api/vendor-item-mappings/`,
            {
                ...getConfig(),
                params: {
                    vendor_id: vendorId,
                    stock_item_id: stockItemId
                }
            }
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Download PO document
export const downloadPODocument = async (id) => {
    try {
        const response = await api.get(
            `/api/purchase-orders/${id}/document/`,
            {
                ...getConfig(),
                responseType: 'blob'
            }
        );
        
        // Get filename from Content-Disposition header
        let filename = `PO_${id}.docx`; // default fallback
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }
        
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        
        // Append to html link element page
        document.body.appendChild(link);
        
        // Start download
        link.click();
        
        // Clean up and remove the link
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Vendor Confirmation API Functions

// Get all confirmations for a purchase order
export const getConfirmedPurchaseOrders = async (poId) => {
    try {
        const response = await api.get(
            `/api/purchase-orders/${poId}/confirmations/`,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Create new confirmation for a purchase order
export const createConfirmedPurchaseOrder = async (poId, confirmationData) => {
    try {
        const userToken = localStorage.getItem('userToken');
        
        // Check if confirmationData is FormData (for atomic document upload)
        const isFormData = confirmationData instanceof FormData;
        
        const config = {
            headers: {
                'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
                Authorization: `Bearer ${userToken}`,
            },
        };
        
        const response = await api.post(
            `/api/purchase-orders/${poId}/confirmations/create/`,
            confirmationData,
            config
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Update confirmation for a purchase order
export const updateConfirmedPurchaseOrder = async (poId, confirmationId, confirmationData) => {
    try {
        const userToken = localStorage.getItem('userToken');
        
        // Check if confirmationData is FormData (for atomic document updates)
        const isFormData = confirmationData instanceof FormData;
        
        const config = {
            headers: {
                'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
                Authorization: `Bearer ${userToken}`,
            },
        };
        
        const response = await api.put(
            `/api/purchase-orders/${poId}/confirmations/${confirmationId}/update/`,
            confirmationData,
            config
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Delete confirmation for a purchase order
export const deleteConfirmedPurchaseOrder = async (poId, confirmationId) => {
    try {
        const response = await api.delete(
            `/api/purchase-orders/${poId}/confirmations/${confirmationId}/delete/`,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Document Management API Functions

// Get all documents for a purchase order
export const getPODocuments = async (poId) => {
    try {
        const response = await api.get(
            `/api/purchase-orders/${poId}/documents/`,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Upload new document for a purchase order
export const uploadPODocument = async (poId, formData) => {
    try {
        const response = await api.post(
            `/api/purchase-orders/${poId}/documents/upload/`,
            formData,
            {
                ...getConfig(),
                headers: {
                    ...getConfig().headers,
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Download a specific document
export const downloadPODocumentById = async (poId, docId) => {
    try {
        const response = await api.get(
            `/api/purchase-orders/${poId}/documents/${docId}/download/`,
            {
                ...getConfig(),
                responseType: 'blob'
            }
        );
        
        // Get filename from Content-Disposition header or use default
        let filename = 'document.pdf';
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }
        
        // Create a URL for the blob
        const url = window.URL.createObjectURL(new Blob([response.data]));
        
        // Create a temporary link and click it
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Delete a specific document
export const deletePODocument = async (poId, docId) => {
    try {
        const response = await api.delete(
            `/api/purchase-orders/${poId}/documents/${docId}/delete/`,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Calculate landed costs for batch receiving
export const calculateLandedCosts = async (confirmedLineIds, markup = 1.5) => {
    try {
        const response = await api.post(
            `/api/purchase-orders/calculate-landed-costs/?markup=${markup}`,
            confirmedLineIds,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

export const completePurchaseOrder = async (poId) => {
    try {
        const response = await api.post(
            `/api/purchase-orders/${poId}/complete/`,
            {}, // No body for completion
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Analyze document content without saving
export const analyzeDocumentContent = async (formData) => {
    try {
        const userToken = localStorage.getItem('userToken');
        const response = await api.post(
            '/api/documents/analyze/',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${userToken}`,
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};

// Convert temporary item to real stock item
export const convertTemporaryItemToStockItem = async (conversionData) => {
    try {
        const response = await api.post(
            '/api/purchase-orders/convert-temporary-item/',
            conversionData,
            getConfig()
        );
        return response.data;
    } catch (error) {
        throw error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message;
    }
};
